// React imports
import React, { useState, useEffect } from 'react';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faTimes, faPaperPlane } from '@fortawesome/pro-light-svg-icons';

// MUI imports
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import ButtonGroup from '@mui/material/ButtonGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

// Local imports
import email from '../utils/email';
import axios from 'axios';
import ReplyEditor from './EmailPage.reply.js';
import flattenThread from '../utils/flattenThread.js';

function Reply(props) {
    const { conversation, onReply, myEmail, onClose, users, organization, tokens, accesstoken, accounts, refreshConversations } = props;

    // Host a pasted screenshot: base64 images do not render in mail clients, which is the
    // whole reason signatures are hosted too.
    async function onUploadImage({ dataUrl, contentType }) {
        const { data } = await axios.post(
            `/api/users/signature-image?accesstoken=${accesstoken}&organizationId=${organization.id}`,
            { image: dataUrl, contentType, prefix: 'email-images' }
        );
        return data?.url;
    }
    const [ to, setTo ] = useState([]);
    const [ cc, setCC ] = useState([]);
    const [ subject, setSubject ] = useState('');
    const [ allRecipients, setAllRecipients ] = useState([]);
    const [ body, setBody ] = useState('');
    const [ sending, setSending ] = useState(false);
    const [ error, setError ] = useState(null);
    const [ includeThread, setIncludeThread ] = useState(_.get(organization, 'integrations.email.includeOriginalThread', true));

    // Helper function to ensure Poppins font is included in email body
    const ensurePoppinsFont = (html) => {
        if (!html) return html;
        // If body already has font-family, use it as-is
        if (html.includes('font-family') || html.includes('fontFamily')) {
            return html;
        }
        // Wrap in a div with Poppins font
        return `<div style="font-family: 'Poppins', sans-serif;">${html}</div>`;
    };

    async function onSend() {
        setError(null);
        setSending(true);
        try {
            // Ensure Poppins font is included in the body
            const bodyWithFont = ensurePoppinsFont(body);
            const result = await email.sendReply({
                org: organization,
                tokens,
                conversationId: _.last(_.compact(conversation.map(c => c.conversationId))),
                toRecipients: to,
                ccRecipients: cc,
                subject,
                body: bodyWithFont,
                attachments: [],
                monadaAccesstoken: accesstoken,
                monadaOrganizationId: organization.id,
                useQueue: true,
            });

            // Email is now queued - pass the pending email info to show in conversation
            setSending(false);

            if (onReply) {
                onReply({
                    queuedEmailId: result.id,
                    pendingMessage: {
                        id: result.id,
                        subject,
                        body: bodyWithFont,
                        toRecipients: to,
                        ccRecipients: cc,
                        status: 'pending'
                    }
                });
            }
            if (refreshConversations) {
                setTimeout(() => refreshConversations(true), 3000);
                setTimeout(() => refreshConversations(true), 8000);
            }
        } catch (e) {
            setSending(false);
            setError(e?.result?.error?.message || e.message);
            console.error(e);
        }
    }

    useEffect(() => {
        const sorted = _.sortBy(conversation.filter(c => c.sender?.emailAddress?.address !== myEmail), a => dayjs(a.receivedDateTime).valueOf())
        const _to = [(_.last(sorted) || _.last(conversation))?.sender?.emailAddress];
        const _cc = [ 
            ...((_.last(sorted) || _.last(conversation))?.toRecipients || []).map(r => r?.emailAddress),
            ...((_.last(sorted) || _.last(conversation))?.ccRecipients || []).map(r => r?.emailAddress),
        ].filter(r => r?.address !== myEmail);
        setTo(_.compact(_to));
        setCC(_.compact(_cc));
        setSubject((_.last(sorted)?.subject || '').startsWith('Re:') ? _.last(sorted)?.subject || '' : `Re: ${_.last(sorted)?.subject || ''}`);
        setAllRecipients(_.uniqBy(_.compact([
            ..._to,
            ..._cc,
            ...(users.map(u => ({ address: u.email, name: u.name }))),
            ...(accounts.map(a => (a.contacts || []).map(c => ({ address: c.email, name: `${c.firstName ? `${c.firstName} ` : ''}${c.lastName ? `${c.lastName}` : ''}${c.firstName || c.lastName ? ' at ' : 'Contact at '}${a.name ? `${a.name}` : ''}` })))).flat(),
        ]), a => a.address));

        // Pre-populate the editor with the flattened thread history
        if (includeThread) {
            const threadHtml = flattenThread(conversation);
            if (threadHtml) {
                setBody(`<div><br/></div><hr/>${threadHtml}`);
            }
        } else {
            setBody('');
        }
    }, [conversation, includeThread])

    return (
        <Box sx={{ background: 'white', borderRadius: 2, mb: 2, border: '1px solid #aaa', position: 'relative' }}>
            <Box sx={{ p: 2, display: 'flex' }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    flex: 1,
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                    '& .MuiChip-label': {
                        fontSize: '11px',
                        fontFamily: 'Poppins, sans-serif',
                    },
                    '& .MuiAutocomplete-option': {
                        fontSize: '11px',
                        fontFamily: 'Poppins, sans-serif',
                    },
                }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography variant='caption' sx={{ width: '60px' }}>To:</Typography>
                        <Autocomplete
                            fullWidth
                            options={_.compact(allRecipients).filter(r => !(_.compact([...(to || []), ...(cc || [])]).map(a => a.address).includes(r.address)))}
                            filterOptions={(options, state) => {
                                return options
                                  .filter((o) =>
                                    o.name?.toLowerCase().includes(state.inputValue.toLowerCase()) || o.address?.toLowerCase().includes(state.inputValue.toLowerCase())
                                  )
                                  .slice(0, 100)
                              }
                            }
                            renderInput={(params) => <TextField {...params} />}
                            multiple
                            freeSolo
                            value={to || []}
                            onChange={(___, value) => {
                                setTo(value.map(v => _.isString(v) ? { address: v } : v))
                            }}
                            onBlur={(e) => {
                                e.target.dispatchEvent(
                                    new KeyboardEvent("keydown", { key: "Enter", bubbles: true })
                                );
                            }}
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                return option.name ? `${option.name} <${option.address}>` : option.address;
                            }}
                            renderInput={(params) => <TextField {...params} />}
                            slotProps={{
                                paper: {
                                    sx: { border: '1px solid #aaa', boxShadow: 'none' }
                                }
                            }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                        <Typography variant='caption' sx={{ width: '60px' }}>Cc:</Typography>
                        <Autocomplete
                            fullWidth
                            options={allRecipients.filter(r => !(_.compact([...(to || []), ...(cc || [])]).map(a => a.address).includes(r.address)))}
                            filterOptions={(options, state) => {
                                return options
                                  .filter((o) =>
                                    o.name?.toLowerCase().includes(state.inputValue.toLowerCase()) || o.address?.toLowerCase().includes(state.inputValue.toLowerCase())
                                  )
                                  .slice(0, 100)
                              }
                            }
                            multiple
                            freeSolo
                            value={cc || []}
                            onChange={(_, value) => setCC(value)}
                            onBlur={(e) => {
                                e.target.dispatchEvent(
                                    new KeyboardEvent("keydown", { key: "Enter", bubbles: true })
                                );
                            }}
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                return option.name ? `${option.name} <${option.address}>` : option.address;
                            }}
                            renderInput={(params) => <TextField {...params} />}
                            slotProps={{
                                paper: {
                                    sx: { border: '1px solid #aaa', boxShadow: 'none' }
                                }
                            }}
                        />
                    </Box>
                </Box>
                <Box>
                    <IconButton onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', p: 2, pt: 0 }}>
                <Typography variant='caption' sx={{ width: '60px' }}>Subject:</Typography>
                <TextField
                    fullWidth
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                />
            </Box>
            <ReplyEditor onBlur={e => setBody(e.html)} initialContent={body} onUploadImage={onUploadImage} />
            {error && (
                <Alert severity='error' sx={{ mt: 2 }}>{error}</Alert>
            )}
            <FormControlLabel
                sx={{ ml: 1 }}
                control={
                    <Checkbox
                        checked={includeThread}
                        onChange={e => setIncludeThread(e.target.checked)}
                        color='primary'
                        size='small'
                    />
                }
                label={<Typography variant='body2'>Include original thread</Typography>}
            />
            <ButtonGroup variant='contained' color='primary' sx={{ m: 1, flex: 1, display: 'flex' }}>
                <Button onClick={onSend} sx={{ width: '100%' }} disabled={sending}>
                    <FontAwesomeIcon icon={faPaperPlane} />&nbsp;&nbsp;Send
                </Button>
                <Button disabled={sending}>
                    <FontAwesomeIcon icon={faPaperclip} />
                </Button>
            </ButtonGroup>
            {sending && (
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: 1
                }}>
                    <CircularProgress size={32} />
                </Box>
            )}
        </Box>
    )
}

export default Reply;